import { FunctionComponent, useState } from "react";
import { toEmailStatus, unhandledEmailStatus } from "@signalist/shared-types";

import { ReactComponent as PhoneIcon } from "@assets/icons/phone.svg";

import { EmailIcon } from "@/components/EmailIcon/EmailIcon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { ProspectScoreBadge } from "@/components/ProspectScoreBadge/ProspectScoreBadge";

import { PersonaCategoryResults } from "@/pages/Private/pages/Apollo/schema/get-people";

import { CustomColumnTypes } from "@/enum/list.enum";

import { classNames } from "@/utils/classNames";

import { getSvgIcons } from "@/pages/Private/pages/ProfileTracker/utils/getIconsByType";

/**
 * Visible height (px) of the slim strip once it has condensed in. The lead
 * panel uses this to offset the sticky tabs row so it sits directly below the
 * strip (mirrors the v81 mockup's `tabsSticky.style.top`).
 */
export const LEAD_PANEL_STRIP_HEIGHT = 52;

const initialsOf = (name: string): string => {
	const parts = name.trim().split(/\s+/);
	const first = parts[0]?.[0] ?? "";
	const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";

	return `${first}${last}`.toUpperCase() || "?";
};

const emailIconStatus = (
	emailStatus?: string | null
): "checked" | "failed" | "refresh" | "retry" | undefined => {
	const status = toEmailStatus(emailStatus);

	// Switch over the shared union so a new status member fails to compile instead of reading as
	// "never searched" (PREC-5035).
	switch (status) {
		case "VALID":
		case "RISKY":
			return "checked";
		case "INVALID":
			return "failed";
		case "RETRY":
			return "retry";
		case "BOUNCED_VALID":
		case "BOUNCED_INVALID":
		case "SEARCHING":
		case null:
			return "refresh";
		default:
			return unhandledEmailStatus(status);
	}
};

/**
 * Slim, sticky summary strip shown at the top of the lead panel from the moment the rep scrolls the
 * lead's name off the top edge.
 *
 * It **overlays** the scrolled content rather than sitting in the scroll flow: the strip lives in a
 * zero-height sticky wrapper, so switching it on displaces nothing below it. In the flow it would
 * push the whole panel body down by its own height at the exact moment it appears — invisible while
 * the strip only ever showed up hundreds of pixels down, a jump on every scroll now that it takes
 * over as soon as the name leaves (PREC-4116).
 *
 * Plain Tailwind / shared components only — safe inside the extension's shadow root.
 */
export const LeadPanelCondensedHeader: FunctionComponent<{
	visible: boolean;
	name?: string | null;
	title?: string | null;
	companyName?: string | null;
	image?: string | null;
	overallScore?: string | null;
	overallReasoning?: string | null;
	scoring?: PersonaCategoryResults | null;
	linkedinUrl?: string | null;
	email?: string | null;
	emailStatus?: string | null;
	phone?: string | null;
}> = ({
	visible,
	name,
	title,
	companyName,
	image,
	overallScore,
	overallReasoning,
	scoring,
	linkedinUrl,
	email,
	emailStatus,
	phone,
}) => {
	const displayName = name ?? "";
	const subtitle = [title, companyName].filter(Boolean).join(" · ");

	// Fall back to initials when the prospect image is missing or fails to load
	// (mirrors the expanded ListPersonHeader's onError behaviour). Tracking the
	// errored src — rather than a boolean reset in an effect — means a new image
	// is retried automatically without resetting state on every render.
	const [erroredSrc, setErroredSrc] = useState<string | null>(null);
	const showImage = !!image && image !== erroredSrc;

	return (
		<div className="sticky top-0 z-20 h-0">
			<div
				className={classNames(
					"flex h-[52px] items-center gap-2 overflow-hidden border-b border-border-light bg-white px-4 py-2 transition-opacity duration-200",
					visible ? "opacity-100" : "pointer-events-none opacity-0"
				)}
				data-testid="lead-panel-condensed-header"
			>
				<ProspectScoreBadge
					overallScore={overallScore}
					reason={overallReasoning}
					scoring={scoring}
					size="sm"
				/>

				<div className="flex h-[28px] min-w-[28px] max-w-[28px] overflow-hidden rounded-full bg-gray-200">
					{showImage ? (
						<img
							alt={displayName}
							className="h-[28px] w-[28px] object-cover"
							src={image}
							onError={() => setErroredSrc(image)}
						/>
					) : (
						<div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-blue-action">
							{initialsOf(displayName)}
						</div>
					)}
				</div>

				<div className="min-w-0 flex-1">
					<div className="truncate text-sm font-semibold text-text">{displayName}</div>
					{subtitle && <div className="truncate text-xs text-text-meta">{subtitle}</div>}
				</div>

				<div className="flex shrink-0 items-center gap-1">
					{linkedinUrl && (
						<a
							className="inline-flex rounded-md border border-gray-200 p-1"
							href={linkedinUrl}
							referrerPolicy="no-referrer"
							rel="noreferrer"
							target="_blank"
						>
							<img className="h-[16px] w-auto" src={getSvgIcons(CustomColumnTypes.LINKEDIN_POST)} />
						</a>
					)}
					{(email || emailStatus === "RETRY") && (
						<span className="inline-flex rounded-md border border-gray-200 p-1">
							<EmailIcon status={emailIconStatus(emailStatus)} />
						</span>
					)}
					{phone && (
						<a
							className="inline-flex rounded-md border border-gray-200 p-1"
							href={`tel:${phone}`}
							referrerPolicy="no-referrer"
							rel="noreferrer"
							target="_blank"
						>
							<SvgIcon className="h-[16px] w-auto" svgIcon={PhoneIcon} />
						</a>
					)}
				</div>
			</div>
		</div>
	);
};
